import { useCallback, useEffect, useState } from 'react';
import type { Outcome } from './api';

export type Loaded<T> =
  | { readonly status: 'loading' }
  | { readonly status: 'failed' }
  | { readonly status: 'ready'; readonly items: readonly T[] };

/** A list read once on mount and again on `reload()` (after a write). */
export function useLoaded<T>(load: () => Promise<Outcome<readonly T[]>>): {
  readonly state: Loaded<T>;
  readonly reload: () => void;
} {
  const [state, setState] = useState<Loaded<T>>({ status: 'loading' });
  const [round, setRound] = useState(0);
  useEffect(() => {
    let live = true;
    void load().then((outcome) => {
      if (!live) return;
      setState(outcome.ok ? { status: 'ready', items: outcome.value } : { status: 'failed' });
    });
    return () => {
      live = false;
    };
    // `load` is a module function or a stable closure of the caller's own.
  }, [round]);
  const reload = useCallback(() => setRound((n) => n + 1), []);
  return { state, reload };
}
